import {
    OrganizationEngagement,
    TopLevelMetrics
} from '../../../../../shared/api/controllers/engagementAnalyticsReporting';

const INBOUND_APPOINTMENT_MANAGED_TAGS = new Set([
    'ACTION_COMPLETED',
    'APPOINTMENT_CANCELLED',
    'APPOINTMENT_RESCHEDULED',
    'CHECK_NEXT_APPOINTMENT_COMPLETE'
]);

const OUTBOUND_APPOINTMENT_MANAGED_TAGS = new Set([
    'OUTBOUND_APPOINTMENT_BOOKED',
    'OUTBOUND-APPOINTMENT-BOOKED',
    'OUTBOUND_APPOINTMENT_RESCHEDULED',
    'OUTBOUND_APPOINTMENT_CANCELLED'
]);

const getMetric = (metrics: TopLevelMetrics | undefined, keys: string[]): number => {
    for (const key of keys) {
        const rawValue = metrics?.[key];
        if (rawValue === null || rawValue === undefined || rawValue === '') continue;
        const value = Number(rawValue);
        if (Number.isFinite(value)) return Math.max(value, 0);
    }
    return 0;
};

export const getOrderPlaceRate = (metrics?: TopLevelMetrics): number | null => {
    const attempts = getOrderAttemptCount(metrics);
    if (attempts <= 0) return null;

    const completed = getPlacedOrderCount(metrics);
    return (completed / attempts) * 100;
};

export const getOrderAttemptCount = (metrics?: TopLevelMetrics): number => getMetric(metrics, [
    'totalOrderAttempts',
    'orderAttempts',
    'containableCalls'
]);

export const getPlacedOrderCount = (metrics?: TopLevelMetrics): number => getMetric(metrics, [
    'totalOrderCompletionEngagements',
    'totalOrdersCompleted',
    'ordersPlaced'
]);

export const getContainmentCounts = (engagements?: OrganizationEngagement[]): {
    contained: number;
    containable: number;
} => {
    const inboundEngagements = (engagements || []).filter(engagement => (
        !(engagement.sessionTags || []).includes('OUTBOUND_CALL')
    ));
    return {
        contained: inboundEngagements.filter(engagement => engagement.isContained).length,
        containable: inboundEngagements.filter(engagement => engagement.isContainable).length
    };
};

export const getContainmentRate = (engagements?: OrganizationEngagement[]): number | null => {
    const counts = getContainmentCounts(engagements);
    if (counts.containable <= 0) return null;

    return (counts.contained / counts.containable) * 100;
};

export const getAppointmentsManagedCount = (engagements?: OrganizationEngagement[]): number => (
    (engagements || []).reduce((total, engagement) => {
        const tags = engagement.sessionTags || [];
        const isOutbound = tags.includes('OUTBOUND_CALL');
        const isOutboundConfirmationRebook = tags.includes('OUTBOUND_APPOINTMENT_CONFIRMED');

        return total + tags.reduce((engagementTotal, tag) => {
            if (OUTBOUND_APPOINTMENT_MANAGED_TAGS.has(tag)) return engagementTotal + 1;
            if (!INBOUND_APPOINTMENT_MANAGED_TAGS.has(tag)) return engagementTotal;
            if (!isOutbound) return engagementTotal + 1;
            if (tag === 'CHECK_NEXT_APPOINTMENT_COMPLETE') return engagementTotal + 1;
            if (tag === 'ACTION_COMPLETED' && isOutboundConfirmationRebook) return engagementTotal + 1;
            return engagementTotal;
        }, 0);
    }, 0)
);

export type InboundAppointmentActivityStatus = 'recent' | 'not-recent' | 'invalid-date' | 'not-applicable';

export const getInboundAppointmentActivityStatus = (
    engagement: OrganizationEngagement,
    windowStartMs: number,
    windowEndMs: number
): InboundAppointmentActivityStatus => {
    const tags = engagement.sessionTags || [];
    if (tags.includes('OUTBOUND_CALL')) return 'not-applicable';
    if (!tags.some(tag => INBOUND_APPOINTMENT_MANAGED_TAGS.has(tag))) return 'not-applicable';

    const eventTime = Date.parse(engagement.eventDate);
    if (!Number.isFinite(eventTime)) return 'invalid-date';
    return eventTime >= windowStartMs && eventTime <= windowEndMs ? 'recent' : 'not-recent';
};

export const isRecentInboundAppointmentManaged = (
    engagement: OrganizationEngagement,
    windowStartMs: number,
    windowEndMs: number
): boolean => {
    return getInboundAppointmentActivityStatus(engagement, windowStartMs, windowEndMs) === 'recent';
};
